import { useEffect, useState } from 'react';
import { useOfficialStore } from '$/store/useOfficialStore';
import useTeamStore from '@/store/useTeamStore';
import { getTeamSettings } from './teamService';
import type { TeamSettings } from './types';

export interface TeamContentPermissions {
  canPublish: boolean;
  canDownload: boolean;
  canDelete: boolean;
}

/**
 * 直接根据服务端下发的团队角色和权限开关判断发布、资产、历史记录权限。
 *
 * - 个人态（`isTeam=false`）保持全部允许
 * - 管理员（`role===1`）不受 settings 影响，一律允许
 * - 普通成员按对应开关判断；settings 缺省时按接口默认值 1 视为允许，避免灰度期误锁
 */
export function resolveTeamContentPermissions(
  isTeam: boolean,
  role: number | undefined,
  settings?: TeamSettings,
): TeamContentPermissions {
  if (!isTeam) {
    return {
      canPublish: true,
      canDownload: true,
      canDelete: true,
    };
  }

  const isAdmin = Number(role) === 1;
  const isOn = (value: 0 | 1 | undefined): boolean => value !== 0;
  return {
    canPublish: isAdmin || isOn(settings?.allow_member_publish),
    canDownload: isAdmin || isOn(settings?.allow_member_download),
    canDelete: isAdmin || isOn(settings?.allow_member_delete),
  };
}

/**
 * 团队态读取一次权限设置；个人态保持原有权限。
 */
export function useTeamPermissions(): TeamContentPermissions {
  const teamOid = useOfficialStore((state) => state.clientOId);
  const role = useTeamStore((state) => state.teamInfo?.role);
  const [settings, setSettings] = useState<TeamSettings>();
  const isTeam = Boolean(teamOid);

  useEffect(() => {
    setSettings(undefined);
    if (!teamOid) return undefined;

    let active = true;
    getTeamSettings()
      .then((data) => {
        if (active) setSettings(data);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [teamOid]);

  return resolveTeamContentPermissions(isTeam, role, settings);
}
